/**
 * Root router component.
 * Defines public and protected routes and wraps the app in BrowserRouter.
 */
import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { ActionsPage } from "@/pages/ActionsPage";
import { BaleBotPage } from "@/pages/BaleBotPage";
import { SmsPage } from "@/pages/SmsPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { DbConnectionsPage } from "@/pages/DbConnectionsPage";
import { HomePage } from "@/pages/HomePage";
import { LoginPage } from "@/pages/LoginPage";
import { ServiceStatusPage } from "@/pages/ServiceStatusPage";

/** Guard that redirects unauthenticated users to the login page. */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span
          className="h-10 w-10 animate-spin rounded-full border-[3px] border-slate-200 border-t-softnu-600 dark:border-slate-700 dark:border-t-softnu-500"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/** Guard that redirects authenticated users away from the login page. */
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span
          className="h-10 w-10 animate-spin rounded-full border-[3px] border-slate-200 border-t-softnu-600 dark:border-slate-700 dark:border-t-softnu-500"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export const App: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ServiceStatusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/flows"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/:flowId?"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/actions"
          element={
            <ProtectedRoute>
              <ActionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bale-bot"
          element={
            <ProtectedRoute>
              <BaleBotPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connections"
          element={
            <ProtectedRoute>
              <DbConnectionsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/sms" element={<ProtectedRoute><SmsPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};
